import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import { Card, Box, Typography, Button, Pagination, TextField, InputAdornment, Skeleton, Paper, Chip } from 'minimal-ui';
import { Iconify } from 'minimal-ui';
import { useQuery } from '@tanstack/react-query';
import { getPromotions } from '../../../../data/promotions';
import {
    PLATFORMS,
    resolveCartPlatform,
    resolveCartPlatformFromRoleProfile,
    getSelectedCustomerFromStorage,
} from '../../../../data/cart';
import { useRole } from '../../../../contexts/RoleContext';
import useDebounce from '../components/useDebounce';
import PromotionDetailsDrawer from '../components/promo-details-drawer';

const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return dateString;
    try {
        const [day, month, year] = dateString.split('/');
        const date = new Date(year, month - 1, day);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${day}-${monthNames[date.getMonth()]}`;
    } catch (e) {
        return dateString;
    }
};
const getPromotionTypeConfig = (typeId) => {
    const configs = {
        "1": { title: "Buy X Get Y Free", backgroundColor: "#E8F5E8", textColor: "#4CAF50" },
        "2": { title: "Buy X Get Y Discount", backgroundColor: "#FFF3E0", textColor: "#FF9800" },
        "3": { title: "Buy X at Discount", backgroundColor: "#E3F2FD", textColor: "#2196F3" },
        "4": { title: "Multi Layer Special", backgroundColor: "#F3E5F5", textColor: "#9C27B0" },
        "5": { title: "Group Offer", backgroundColor: "#FFE0E1", textColor: "#E27172" }
    };
    return configs[typeId] || { title: "Special Offer", backgroundColor: "#FFE0E1", textColor: "#E27172" };
};

const getProductPromoGroupId = (product) => {
    const v = product?.promoGroupId ?? product?.promo_group_id;
    if (v === undefined || v === null || String(v).trim() === '') return null;
    return String(v);
};

const MS_PER_MINUTE = 60 * 1000;
const MINUTES_PER_DAY = 1440;
const ENDING_SOON_MAX_MINUTES = 4320;
const NEWEST_MAX_MINUTES = 10080;

const parsePromotionDate = (value, boundary = "start") => {
    if (value === undefined || value === null || value === "") return null;
    const str = String(value).trim();
    if (!str || str === "N/A") return null;

    const hasExplicitTime = /[T\s]\d{1,2}:\d{2}/.test(str);

    if (str.includes("/") && !hasExplicitTime) {
        const [day, month, year] = str.split("/").map(Number);
        if (!day || !month || !year) return null;
        const date = new Date(year, month - 1, day);
        if (boundary === "end") {
            date.setHours(23, 59, 59, 999);
        } else {
            date.setHours(0, 0, 0, 0);
        }
        return date;
    }

    const parsed = new Date(str);
    if (Number.isNaN(parsed.getTime())) return null;

    if (!hasExplicitTime) {
        if (boundary === "end") {
            parsed.setHours(23, 59, 59, 999);
        } else {
            parsed.setHours(0, 0, 0, 0);
        }
    }

    return parsed;
};

const isBetweenInclusive = (value, min, max) => value >= min && value <= max;

const getPromotionTimingFlags = (promotion, now = new Date()) => {
    const start = parsePromotionDate(promotion.start_date_raw || promotion.start_date, "start");
    const end = parsePromotionDate(promotion.end_date_raw || promotion.end_date, "end");

    if (!start || !end) {
        return {
            minutesUntilEnd: null,
            daysUntilEnd: null,
            minutesSinceStart: null,
            isEndingSoon: false,
            isNewest: false,
            isActive: true,
        };
    }

    const minutesUntilEnd = Math.ceil((end.getTime() - now.getTime()) / MS_PER_MINUTE);
    const daysUntilEnd = Math.ceil(minutesUntilEnd / MINUTES_PER_DAY);
    const minutesSinceStart = Math.ceil((now.getTime() - start.getTime()) / MS_PER_MINUTE);

    const isEndingSoon = isBetweenInclusive(minutesUntilEnd, 0, ENDING_SOON_MAX_MINUTES);
    const isNewest = isBetweenInclusive(minutesSinceStart, 0, NEWEST_MAX_MINUTES);
    const isActive = minutesSinceStart >= 0 && minutesUntilEnd >= 0;

    return {
        minutesUntilEnd,
        daysUntilEnd,
        minutesSinceStart,
        isEndingSoon,
        isNewest,
        isActive,
    };
};

const getPromotionBadge = (timing) => {
    if (!timing) return null;
    if (timing.isEndingSoon) {
        const daysLeft = Math.max(timing.daysUntilEnd, 0);
        return {
            type: "Ending Soon",
            label: `ENDING SOON \u2013 ${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`,
        };
    }
    if (timing.isNewest) {
        return { type: "Newest", label: "NEWEST" };
    }
    return null;
};

const filterPromotionsByTab = (promotions, activeFilter) => {
    return promotions.filter((promotion) => {
        const timing = promotion.timing;
        if (timing && timing.isActive === false) {
            return false;
        }

        if (activeFilter === "Ending Soon") {
            return timing?.isEndingSoon === true;
        }
        if (activeFilter === "Newest") {
            return timing?.isNewest === true && timing?.isEndingSoon !== true;
        }
        return true;
    });
};

export default function OfferPage() {

    const navigate = useNavigate();
    const { activeRole, hasPermission } = useRole();


    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilter, setActiveFilter] = useState('All');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPromotion, setSelectedPromotion] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 500);

    const getFilterApiValue = (filter) => {
        const filterMap = {
            'All': 'all',
            'Newest': 'all',
            'Ending Soon': 'all',
            'Continue to Order': 'continue_to_order'
        };
        return filterMap[filter] || 'all';
    };

    const apiFilter = getFilterApiValue(activeFilter);
    const cartPlatform =
        resolveCartPlatform({}) ??
        resolveCartPlatformFromRoleProfile({
            roleName: activeRole?.name || "",
            hasPOSPermission: hasPermission("POS"),
            hasSalesQuickOrderPermission: hasPermission("SalesQuickOrder"),
        }) ??
        PLATFORMS.TELE_CALLING;
    const selectedCustomer = getSelectedCustomerFromStorage(cartPlatform);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, apiFilter]);

    const { data: promotionsData, isLoading, error } = useQuery({
        queryKey: ['promotions', currentPage, debouncedSearch, apiFilter, cartPlatform],
        queryFn: () => getPromotions(currentPage, 12, debouncedSearch, apiFilter, { channels: cartPlatform }),
    });

    const promotionsRaw = promotionsData?.data?.promotions || [];
    const promotionsWithTiming = promotionsRaw.map((promotion) => ({
        ...promotion,
        timing: getPromotionTimingFlags(promotion),
    }));
    const promotions = filterPromotionsByTab(promotionsWithTiming, activeFilter);
    const pagination = promotionsData?.data?.pagination || {};
    const totalPages = pagination.total_pages || 1;

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };

    const handleViewMore = (promotion) => {
        setSelectedPromotion(promotion);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setSelectedPromotion(null);
    };

    if (error) {
        return (
            <div>

                <div className="main-container position-relative" style={{ backgroundColor: "#F8F8F8", minHeight: "calc(100vh - 32px)" }}>
                    <div className="container-fluid">
                        <Paper variant="elevation" elevation={2} sx={{ p: 2 }}>
                            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                                <Typography color="error">Error loading promotions. Please try again.</Typography>
                            </Box>
                        </Paper>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <Box sx={{ px: 2, py: 1.2, backgroundColor: "#F5F7FA" }}>
                <Paper variant="elevation" elevation={2} sx={{ p: 2, mb: 5 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                className="form-input-border"
                                variant="outlined"
                                placeholder="Search by Product Name or Code and Promotion Title"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#000000',
                                        '& fieldset': {
                                            borderColor: '#000000',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#000000',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#000000',
                                        },
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: '#000000',
                                        opacity: 1,
                                    },
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Iconify
                                                    icon="eva:search-fill"
                                                    sx={{ color: '#000000' }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                size="large"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1, mb: 2, justifyContent: 'flex-end' }}>
                                {['All','Ending Soon', 'Continue to Order'].map((filter) => (
                                    <Button
                                        key={filter}
                                        size='medium'
                                        onClick={() => {
                                            if (filter === 'Continue to Order') {
                                                navigate('/sales/quick-order');
                                            } else {
                                                setActiveFilter(filter);
                                            }
                                        }}
                                        sx={{
                                            borderRadius: '20px',
                                            px: 3,
                                            py: 1,
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            backgroundColor: activeFilter === filter ? '#FF6B6B' : 'white',
                                            color: activeFilter === filter ? 'white' : '#666',
                                            border: activeFilter === filter ? 'none' : '1px solid rgba(22, 22, 22, 0.83)',
                                            '&:hover': {
                                                backgroundColor: activeFilter === filter ? '#FF5252' : '#F5F5F5',
                                                border: activeFilter === filter ? 'none' : '1px solid rgba(22, 22, 22, 0.83)',
                                            }
                                        }}
                                    >
                                        {filter}
                                    </Button>
                                ))}
                            </Box>
                        </Grid>
                        {isLoading ? (
                            <>
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <Grid key={index} size={{ xs: 12, sm: 6, md: 6, xl: 4 }}>
                                        <Card sx={{
                                            borderRadius: '16px',
                                            padding: '16px',
                                            boxShadow: '0px 0px 4px 0px #0000001F',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            maxHeight: '400px',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}>
                                            <Skeleton
                                                variant="rectangular"
                                                width={45}
                                                height="100%"
                                                sx={{
                                                    position: 'absolute',
                                                    right: 0,
                                                    top: 0,
                                                    borderRadius: '16px 0 0 16px'
                                                }}
                                            />
                                            <Box sx={{ pr: 5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', mt: 2 }}>
                                                <Box>
                                                    <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
                                                    <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
                                                    <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
                                                    <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1 }} />
                                                    <Box sx={{
                                                        backgroundColor: '#F7F7F7',
                                                        borderRadius: '10px',
                                                        padding: '10px',
                                                        mt: 1,
                                                        minHeight: '120px'
                                                    }}>
                                                        {Array.from({ length: 3 }).map((_, i) => (
                                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                                <Skeleton variant="circular" width={16} height={16} sx={{ mr: 1 }} />
                                                                <Skeleton variant="text" width="70%" height={16} />
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                                <Box mt={2}>
                                                    <Skeleton variant="rectangular" height={40} sx={{ borderRadius: '8px' }} />
                                                </Box>
                                            </Box>
                                        </Card>
                                    </Grid>
                                ))}
                                <Grid size={{ xs: 12 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                                        <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: '8px' }} />
                                    </Box>
                                </Grid>
                            </>
                        ) : (
                            <>
                                {promotions.map((promotion) => {
                                    const typeConfig = getPromotionTypeConfig(promotion.type_id);
                                    const badge = activeFilter === 'All' ? getPromotionBadge(promotion.timing) : null;
                                    const buyProducts = (promotion.buy_products || []).filter(product =>
                                        product.product_name && product.product_name.trim() !== ''
                                    );
                                    const displayProducts = buyProducts.slice(0, 3);
                                    const hasValidProducts = displayProducts.length > 0;
                                    const validProductsCount = buyProducts.length;

                                    return (
                                        <Grid key={promotion.id} size={{ xs: 12, sm: 6, md: 6, xl: 4 }}>
                                            <Card sx={{
                                                borderRadius: '16px',
                                                border: `1px solid ${typeConfig.backgroundColor}`,
                                                padding: '16px',
                                                boxShadow: '0px 0px 4px 0px #0000001F',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                maxHeight: '400px',
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}>
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        right: 0,
                                                        top: 0,
                                                        bottom: 0,
                                                        width: '45px',
                                                        backgroundColor: typeConfig.backgroundColor,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transformOrigin: 'right center',
                                                        whiteSpace: 'nowrap',
                                                        zIndex: 1
                                                    }}
                                                >
                                                    <Typography
                                                        variant='h4'
                                                        sx={{
                                                            color: typeConfig.textColor,
                                                            fontWeight: 700,
                                                            transform: 'rotate(180deg)',
                                                            writingMode: 'vertical-rl'
                                                        }}
                                                    >
                                                        {typeConfig.title}
                                                    </Typography>
                                                </Box>
                                                {badge && (
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 20,
                                                        width: badge.type === 'Newest' ? 100 : 190,
                                                        height: 30,
                                                        borderBottomRightRadius: 7,
                                                        borderBottomLeftRadius: 7,
                                                        backgroundColor: badge.type === 'Ending Soon' ? 'error.dark' : 'success.dark',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        zIndex: 2
                                                    }}>
                                                        <Typography
                                                            variant='body2'
                                                            color='white'
                                                            fontWeight={500}
                                                            fontSize={12}
                                                        >
                                                            {badge.label}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                <Box sx={{ pr: 5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', mt: 2 }}>
                                                    <Box>
                                                        <Typography
                                                            variant='body1'
                                                            fontWeight={500}
                                                            sx={{
                                                                mb: 1,
                                                                // display: '-webkit-box',
                                                                // WebkitLineClamp: 1,
                                                                // WebkitBoxOrient: 'vertical',
                                                                // overflow: 'hidden',
                                                                // lineHeight: 1.4,
                                                                // minHeight: '2.8em'
                                                            }}
                                                        >
                                                            {promotion.title}
                                                        </Typography>
                                                        <Typography variant='body2' color='text.primary' fontWeight={500} sx={{ mb: 1 }}>
                                                            Valid: {formatDate(promotion.start_date)} to {formatDate(promotion.end_date)}
                                                        </Typography>
                                                        <Typography variant='body2' color='text.primary' fontWeight={500} sx={{ mb: 1 }}>
                                                            Included Products ({validProductsCount})
                                                        </Typography>
                                                        <Box sx={{
                                                            backgroundColor: hasValidProducts ? '#F7F7F7' : 'white',
                                                            borderRadius: '10px',
                                                            padding: '10px',
                                                            mt: 1,
                                                            minHeight: '120px'
                                                        }}>
                                                            {displayProducts.map((product, index) => {
                                                                const groupId = getProductPromoGroupId(product);
                                                                return (
                                                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.5, minWidth: 0 }}>
                                                                        <Iconify icon="mingcute:check-line" width={16} color="#666" style={{ marginRight: '8px', flexShrink: 0 }} />
                                                                        <Typography variant='body2' fontSize='12px' sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            {product.product_code} - {product.product_name}
                                                                        </Typography>
                                                                        {groupId ? (
                                                                            <Chip
                                                                                label={`Group ${groupId}`}
                                                                                size="small"
                                                                                sx={{
                                                                                    flexShrink: 0,
                                                                                    height: 22,
                                                                                    fontSize: '11px',
                                                                                    fontWeight: 600,
                                                                                    borderRadius: '999px',
                                                                                    backgroundColor: '#FFF9C4',
                                                                                    color: '#5D4E00',
                                                                                    border: '1px solid #FFC107',
                                                                                    '& .MuiChip-label': { px: 1 },
                                                                                    '&:hover': {
                                                                                        backgroundColor: '#FFF9C4 !important',
                                                                                        color: '#5D4E00 !important',
                                                                                        border: '1px solid #FFC107 !important',
                                                                                    }
                                                                                }}
                                                                            />
                                                                        ) : null}
                                                                    </Box>
                                                                );
                                                            })}
                                                            {buyProducts.length >= 3 && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Iconify icon="mingcute:check-line" width={16} color="#F7F7F7" style={{ marginRight: '8px' }} />
                                                                    <Typography variant='body2' fontSize='12px'>...</Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <Box mt={2}>
                                                        <Button
                                                            onClick={() => handleViewMore(promotion)}
                                                            sx={{
                                                                '&.MuiButton-root': {
                                                                    backgroundColor: '#000',
                                                                    color: '#fff',
                                                                    border: '1px solid #000',
                                                                    '&:hover': {
                                                                        backgroundColor: '#333',
                                                                        borderColor: '#333'
                                                                    }
                                                                }
                                                            }}
                                                            variant='contained'
                                                            fullWidth
                                                        >
                                                            View More
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </>
                        )}
                        <PromotionDetailsDrawer
                            open={drawerOpen}
                            onClose={handleCloseDrawer}
                            promotion={selectedPromotion}
                            selectedCustomer={selectedCustomer}
                            platform={cartPlatform}
                        />
                        {!isLoading && (
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="black"
                                        size="large"
                                    />
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            </Box>
        </>
    );
}
